import React from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Button, ButtonToolbar, Modal } from 'reactstrap';
import { CP_PRODUCTS } from '@/shared/helpers/constants';
import api from '@/utils/apiClient';
import * as Constants from '@/shared/helpers/constants';
import LoadingIcon from 'mdi-react/LoadingIcon';
import { getCPUsersList } from '@/containers/Control-Panel/redux/user/actions';
import { getCPCohortsList } from '@/containers/Control-Panel/redux/cohort/actions';
import { getCPOrganizationsList } from '@/containers/Control-Panel/redux/organization/actions';
import { useHistory } from 'react-router-dom';
import { paths } from '@/shared/helpers/paths';
import { uncheckAllItem } from '@/containers/Control-Panel/Assigned/redux/actions';

const controlPanelAssignBaseUrl = paths.CONTROL_PANEL.ASSIGN;

const UnassignDialog = ({ show, onHide }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { rtl } = useSelector((state) => state, shallowEqual);
  const { assignedProduct, assignedOrgId } = useSelector(
    (state) => state.controlPanel,
    shallowEqual,
  );
  const { activeTab, checkedItemIds } = useSelector((state) => state.assignedPage, shallowEqual);
  const { filters: userFilters, searchAssigned: userSearch } = useSelector(
    (state) => state.cpUsers,
    shallowEqual,
  );
  const { filters: cohortFilters, searchAssigned: cohortSearch } = useSelector(
    (state) => state.cpCohorts,
    shallowEqual,
  );
  const { filters: orgFilters, searchAssigned: orgSearch } = useSelector(
    (state) => state.cpOrgs,
    shallowEqual,
  );

  const initialState = {
    isOpen: show,
    title: 'Unassignment Confirmation',
    message: 'Are you sure want to proceed forward with unassignment?',
    hide: onHide,
    loading: false,
    unassignmentSuccess: false,
    unassignmentError: false,
  };
  const [modal, setModal] = React.useState(initialState);

  const handleOnClose = () => {
    const filter = { assigned: true, feature: assignedProduct?.id, orgId: assignedOrgId };
    if (activeTab === Constants.USERS) {
      dispatch(getCPUsersList({ ...userFilters, ...userSearch, ...filter }));
    } else if (activeTab === Constants.COHORT) {
      dispatch(getCPCohortsList({ ...cohortFilters, ...cohortSearch, ...filter }));
    } else if (activeTab === Constants.ORGANIZATION) {
      dispatch(getCPOrganizationsList({ ...orgFilters, ...orgSearch, ...filter }));
    }
    setModal({ ...initialState });
    dispatch(uncheckAllItem());
    onHide();
  };

  const getSelectedIds = () => {
    switch (activeTab) {
      case Constants.USERS:
        return { orgIds: [assignedOrgId], userIds: checkedItemIds };
      case Constants.COHORT:
        return { orgIds: [assignedOrgId], cohortIds: checkedItemIds };
      case Constants.ORGANIZATION:
        return { orgIds: checkedItemIds };
    }
  };

  const performUnassignment = async () => {
    setModal({
      ...modal,
      loading: true,
      title: 'Performing Unassignment',
      message: 'Unassignment in progress...',
    });

    const { DDO, ACCELTIC, CHATBOT } = CP_PRODUCTS;
    const { orgIds = [], userIds = [], cohortIds = [] } = getSelectedIds();

    /*Make api call for performing assignment*/
    let response = null;
    if (assignedProduct === ACCELTIC) {
      response = await api.assigment.setAssignmentAcceltic(orgIds, cohortIds, userIds, false);
    } else if (assignedProduct === DDO) {
      response = await api.assigment.setAssignmentForDDO(orgIds, cohortIds, userIds, false);
    } else if (assignedProduct === CHATBOT) {
      response = await api.assigment.setAssignmentForChatbot(orgIds, cohortIds, userIds, false);
    }

    /*update modal properties based on response*/
    let updatedModal = {};
    if (response !== null) {
      if (response.isSuccess) {
        updatedModal = {
          loading: false,
          unassignmentSuccess: true,
          title: 'Done',
          message: 'Unassignment completed successfully!',
        };
      } else {
        updatedModal = {
          loading: false,
          unassignmentError: true,
          title: 'Opps',
          message: 'Error performing unassignment!',
        };
      }
    } else {
      updatedModal = {
        loading: false,
        unassignmentError: true,
        title: 'Error',
        message: 'Unassignment is only supported for Acceltic, DDO and Chatbot',
      };
    }
    setModal({ ...modal, ...updatedModal });
  };

  const goToAssign = () => {
    history.push(
      `${controlPanelAssignBaseUrl}?activeTab=${activeTab}&unassignmentFor=${assignedProduct.id}`,
    );
  };

  return (
    <Modal
      isOpen={show}
      toggle={modal.hide}
      keyboard={false}
      backdrop={'static'}
      modalClassName={`${rtl.direction}-support`}
      className="modal-dialog--primary modal-dialog"
    >
      <div className="modal__header">
        <button
          className="lnr lnr-cross modal__close-btn"
          aria-label="modal__close-btn"
          type="button"
          onClick={handleOnClose}
        />
        {!(modal.loading || modal.unassignmentSuccess || modal.unassignmentError) && (
          <span className="lnr lnr-unlink modal__title-icon" />
        )}
        {modal.unassignmentSuccess && (
          <span className="lnr lnr-thumbs-up modal__title-icon success" />
        )}
        {modal.unassignmentError && (
          <span className="lnr lnr-cross-circle modal__title-icon error" />
        )}
        <h4 className="text-modal  modal__title">{modal.title}</h4>
        <div className="modal__body">
          {modal.loading && (
            <div className="panel__refresh position-relative" style={{ height: 75 }}>
              <LoadingIcon />
            </div>
          )}
          {modal.message}
        </div>
        <ButtonToolbar className="modal__footer">
          {modal.unassignmentError && (
            <Button className="modal_cancel" onClick={handleOnClose} outline>
              Close
            </Button>
          )}
          {modal.unassignmentSuccess && (
            <>
              <Button className="modal_cancel" onClick={handleOnClose} outline>
                Okay
              </Button>
              <Button className="modal_ok" outline color="primary" onClick={goToAssign}>
                Go to Assign
              </Button>
            </>
          )}
          {!(modal.unassignmentSuccess || modal.unassignmentError || modal.loading) && (
            <>
              <Button className="modal_cancel" onClick={handleOnClose}>
                Cancel
              </Button>
              <Button className="modal_ok" outline color="danger" onClick={performUnassignment}>
                Yes, Unassign
              </Button>
            </>
          )}
        </ButtonToolbar>
      </div>
    </Modal>
  );
};

export default UnassignDialog;
